import type { CharacterKind } from '@/components'
import { NetClient } from '@/net/NetClient'
import { DEFAULT_ROOM_CONFIG, type ErrorCode, type RoomView } from '@/net/protocol'
import { RoomScreen } from './RoomScreen'

/** What room access needs from the game. */
export interface OnlineHost {
  /** Hand the screen to this connection's room (again, after a reconnect). */
  play(client: NetClient): void
  /** The match ended or was stopped: clear the arena for the room screen. */
  between(): void
  /** Back to playing on this screen alone, at the main menu. */
  quit(): void
}

const NICK_KEY = 'voidfall.nick'
const TOKEN_KEY = 'voidfall.token'
/** Pause before the n-th attempt to come back, growing to this at most. */
const RETRY_MAX_MS = 5000
/** How long a returning tab waits for its old room before giving up on it. */
const RESUME_WAIT_MS = 2000

/**
 * Getting into a room and staying in it.
 *
 * Owns the connection: creating or joining a room, the link a friend opens,
 * what to do with the server's refusals, and coming back after the socket
 * drops or the tab reloads. The server keeps the slot while the room lives,
 * and the token in this tab's session storage claims it again — so this keeps
 * trying for as long as the player stays (docs/ONLINE.md §7.7).
 */
export class RoomAccess {
  private readonly screen: RoomScreen
  /** The connection commands go to. */
  private client: NetClient | null = null
  /** The connection the game is drawing from. */
  private bound: NetClient | null = null
  /** Connections the server accepted; only losing one of those is a drop. */
  private readonly welcomed = new WeakSet<NetClient>()
  private leaving = false

  constructor(
    parent: HTMLElement,
    portraits: () => Record<CharacterKind, string>,
    private readonly game: OnlineHost,
  ) {
    this.screen = new RoomScreen(parent, portraits, {
      create: (nick) => void this.enter(nick, (client) => client.create(DEFAULT_ROOM_CONFIG)),
      join: (nick, code) => void this.enter(nick, (client) => client.join(code)),
      rename: (nick) => {
        localStorage.setItem(NICK_KEY, nick)
        this.client?.rename(nick)
      },
      back: () => {
        this.close()
        this.screen.show('none')
        this.game.quit()
      },
      pick: (kind) => this.client?.pick(kind),
      watch: (on) => this.client?.watch(on),
      configure: (config) => this.client?.configure(config),
      start: () => this.client?.start(),
      abort: () => this.client?.abort(),
      leave: () => this.leave(),
      resume: () => this.screen.show('none'),
    })
  }

  /** Where this browser stands online, for the screenshot harness. */
  status(): { host: boolean; playing: boolean; screen: string } | null {
    const room = this.client?.room
    if (!room) return null
    return { host: room.host === room.you, playing: room.playing, screen: this.screen.shown }
  }

  /** The entry screen, from the main menu. */
  open(message = ''): void {
    this.screen.showEntry(storedNick(), '', message)
  }

  /**
   * On page load: a `#room=CODE` link joins that room; otherwise a token
   * left by this tab before a reload tries to take its slot back.
   */
  startup(): void {
    const code = /(?:^|[#&])room=([A-Za-z0-9]+)/.exec(location.hash)?.[1]
    if (code) {
      history.replaceState(null, '', location.pathname + location.search)
      void this.enter(storedNick(), (client) => client.join(code.toUpperCase()))
      return
    }
    const token = sessionStorage.getItem(TOKEN_KEY)
    if (token) void this.resume(token)
  }

  /** Esc during a match: the match goes on; this only opens or closes the menu. */
  toggleMatchMenu(): void {
    if (this.screen.shown === 'match') this.screen.show('none')
    else if (this.screen.shown === 'none') this.screen.showMatchMenu()
  }

  showResults(winner: string, score: number[]): void {
    this.screen.showResults(winner, score)
  }

  private async enter(nick: string, then: (client: NetClient) => void): Promise<void> {
    const name = nick || storedNick()
    localStorage.setItem(NICK_KEY, name)
    this.close()
    const client = this.connect(name, null)
    try {
      await client.connect()
    } catch (error) {
      if (this.client === client) this.client = null
      this.open(connectFailure(error))
      return
    }
    this.welcomed.add(client)
    then(client)
  }

  private async resume(token: string): Promise<void> {
    const client = this.connect(storedNick(), token)
    try {
      await client.connect()
    } catch {
      if (this.client === client) this.client = null
      return
    }
    this.welcomed.add(client)
    await new Promise((resolve) => setTimeout(resolve, RESUME_WAIT_MS))
    // The room is gone, or the slot was given up: nothing to resume.
    if (this.client === client && client.room === null) {
      sessionStorage.removeItem(TOKEN_KEY)
      this.close()
    }
  }

  private connect(nick: string, token: string | null): NetClient {
    const url = new URL('ws', location.href)
    url.protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
    url.hash = ''
    const client = new NetClient({ url: url.href, nick, token })
    this.client = client
    this.leaving = false

    client.on('room', (view) => this.onRoom(client, view))
    client.on('error', (code, message) => this.onError(code, message))
    client.on('close', () => {
      if (this.welcomed.has(client) && client === this.client && !this.leaving) {
        void this.reconnect(client)
      }
    })
    return client
  }

  private onRoom(client: NetClient, view: RoomView): void {
    if (client !== this.client) return
    if (client.token) sessionStorage.setItem(TOKEN_KEY, client.token)
    if (this.bound !== client) {
      this.bound = client
      this.game.play(client)
    }
    this.screen.update(view)
    if (view.playing) {
      // Into the match, unless the player has its menu open over it.
      if (this.screen.shown === 'room' || this.screen.shown === 'entry') this.screen.show('none')
    } else if (this.screen.shown !== 'room') {
      this.game.between()
      this.screen.show('room')
    }
  }

  private onError(code: ErrorCode, message?: string): void {
    if (code === 'version') {
      // A cached build meeting a newer server: fetch the new one.
      void navigator.serviceWorker
        ?.getRegistration()
        .then((registration) => registration?.update())
        .finally(() => location.reload())
      return
    }
    this.screen.message(message ?? code)
  }

  /**
   * The socket dropped: come back to the same slot. Keeps trying — a laptop
   * lid closed for a minute still finds its slot — until the player leaves or
   * the server says the room is gone.
   */
  private async reconnect(lost: NetClient): Promise<void> {
    const token = lost.token
    for (let attempt = 1; ; attempt++) {
      this.screen.message(`СВЯЗЬ ПОТЕРЯНА · ПЕРЕПОДКЛЮЧЕНИЕ (ПОПЫТКА ${attempt})`)
      await new Promise((resolve) => setTimeout(resolve, Math.min(RETRY_MAX_MS, 600 * attempt)))
      if (this.client !== lost) return
      const next = this.connect(storedNick(), token)
      try {
        await next.connect()
      } catch {
        if (this.client !== next) return
        this.client = lost
        continue
      }
      this.welcomed.add(next)
      await new Promise((resolve) => setTimeout(resolve, RESUME_WAIT_MS))
      if (this.client !== next) return
      if (next.room !== null) {
        this.screen.message('')
        return
      }
      // Connected, but the slot is gone: the server restarted, or the match
      // ended and the room forgot this player while they were away.
      this.drop('Комната закрылась — создайте новую или войдите по коду')
      return
    }
  }

  private drop(message: string): void {
    sessionStorage.removeItem(TOKEN_KEY)
    this.close()
    this.screen.show('none')
    this.game.quit()
    this.open(message)
  }

  private leave(): void {
    this.client?.leave()
    sessionStorage.removeItem(TOKEN_KEY)
    this.close()
    this.screen.show('none')
    this.game.quit()
  }

  private close(): void {
    this.leaving = true
    this.client?.close()
    this.client = null
    this.bound = null
  }
}

function storedNick(): string {
  const saved = localStorage.getItem(NICK_KEY)?.trim()
  if (saved) return saved
  // Short, pronounceable and unlikely to repeat in a room of six.
  const words = ['Лис', 'Ёж', 'Бобр', 'Сыч', 'Волк', 'Лось', 'Грач', 'Барс']
  return `${words[Math.floor(Math.random() * words.length)]}${10 + Math.floor(Math.random() * 90)}`
}

function connectFailure(error: unknown): string {
  const code = error instanceof Error ? error.message : ''
  if (code === 'version') return 'Игра обновилась — перезагрузите страницу'
  return 'Не удалось подключиться к серверу'
}
